import {
  type ShortCode,
  type EnrichmentContext,
  fetchContributorsByCategory,
} from '@highereducation/wordpress';
import type { LibContributorCard } from '@highereducation/wordpress';
import { ExpertRow } from '@highereducation/hen-ui';
import { contributorToExpertItem } from '../../utils/contributorToExpertItem';

type ExpertRowSize = 'lg' | 'sm';
type ExpertRowBorder = 'none' | 'paddingOnly';

const VALID_GAPS = ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'] as const;
type ExpertRowGap = (typeof VALID_GAPS)[number];

const VALID_HEADING_CONTEXTS = ['default', 'section'] as const;
type ExpertRowHeadingContext = (typeof VALID_HEADING_CONTEXTS)[number];

interface ExpertRowShortcodeProps {
  /** Single contributorsCategory slug (e.g. "writer") */
  category?: string;
  /** Contributor databaseIds, comma-separated (e.g. "123,345") — takes priority over category */
  ids?: string;
  /** Max experts to display — omit to show all fetched contributors */
  limit?: string;
  /** ExpertBio size for every card (default: sm) */
  size?: ExpertRowSize;
  /** ExpertBio border styling for every card (default: none) */
  border?: ExpertRowBorder;
  /** Grid gap between cards (default: lg) */
  gap?: string;
  /**
   * Document-outline role for expert names (`default` = h5, `section` = h2 under a page heading)
   */
  'heading-context'?: string;
  [key: string]: unknown;
}

function parseLimit(val: string | undefined): number | undefined {
  if (!val) return undefined;
  const n = Number(val);
  if (Number.isNaN(n) || n < 1) return undefined;
  return Math.floor(n);
}

function parseGap(val: unknown): ExpertRowGap {
  if (
    typeof val === 'string' &&
    (VALID_GAPS as readonly string[]).includes(val)
  ) {
    return val as ExpertRowGap;
  }
  return 'lg';
}

function parseHeadingContext(val: unknown): ExpertRowHeadingContext {
  if (
    typeof val === 'string' &&
    (VALID_HEADING_CONTEXTS as readonly string[]).includes(val)
  ) {
    return val as ExpertRowHeadingContext;
  }
  return 'default';
}

/**
 * ExpertRowShortcode — renders a paginated grid of ExpertBio cards populated
 * from the CMS. Each card is a clickable link to the expert's profile page.
 * Fetches experts in a single batch query via `fetchContributorsByCategory`.
 *
 * Supported shortcode attributes:
 * - ids: string - Contributor databaseIds, comma-separated — takes priority over category
 * - category: string - Single contributorsCategory slug (e.g. "writer")
 * - limit: string - Max experts to show (default: all)
 * - size: 'lg' | 'sm' - ExpertBio card size (default: sm)
 * - border: 'none' | 'paddingOnly' - ExpertBio card border (default: none)
 * - gap: gap token - Grid gap between cards (default: lg)
 * - heading-context: 'default' | 'section' - Name outline role (default: default). Use `section` when the row is primary content under a page-level heading.
 *
 * @example Fetch by IDs:
 * [hen-ui-expert-row ids="123,345"][/hen-ui-expert-row]
 *
 * @example Fetch by category:
 * [hen-ui-expert-row category="writer" limit="6" size="sm" border="paddingOnly"][/hen-ui-expert-row]
 *
 * @example Latest experts:
 * [hen-ui-expert-row limit="9" size="sm"][/hen-ui-expert-row]
 *
 * @example Contributor directory (section-level names):
 * [hen-ui-expert-row limit="9" size="sm" heading-context="section"][/hen-ui-expert-row]
 */
export const ExpertRowShortcode: ShortCode<
  ExpertRowShortcodeProps,
  LibContributorCard[]
> = {
  getEnrichedData: async (
    _content: string,
    context?: EnrichmentContext,
  ): Promise<LibContributorCard[]> => {
    const attrs = context?.attrs ?? {};
    const config = context?.config;
    if (!config?.cmsUrl) return [];

    const limit = parseLimit(attrs['limit']);
    const ids = attrs['ids'];
    const category = attrs['category'];

    if (ids) {
      const parsedIds = String(ids)
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      return fetchContributorsByCategory(config, { ids: parsedIds, limit });
    }

    if (category) {
      return fetchContributorsByCategory(config, {
        category: category.trim(),
        limit,
      });
    }

    return fetchContributorsByCategory(config, { limit });
  },

  component: ({
    size = 'sm',
    border = 'none',
    gap,
    'heading-context': headingContext,
    enrichments,
  }) => {
    const experts = (enrichments?.data ?? []) as LibContributorCard[];
    if (!experts.length) return null;

    return (
      <ExpertRow
        experts={experts.map(contributorToExpertItem)}
        size={size}
        border={border}
        gap={parseGap(gap)}
        headingContext={parseHeadingContext(headingContext)}
      />
    );
  },
};
